import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import BreadCrumbsJsonLd from './BreadCrumbsJsonLd';
import useHorizontalScroll from './horizontal-scroll.hook';
import { getUri } from 'services/generic';
import { capitalize, getFormattedMetaData } from 'services/formatter';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { Hierarchy } from 'types/Hierarchy';
import { ProductBreadcrumbType } from 'types/ProductPageV2/Products';

import styles from './BreadCrumbs.module.scss';

/**
 * Breadcrumb use at the top of the different pages (CategoryPage, SearchPage, ProductPage, etc).
 */
export default function BreadCrumbs({
  breadCrumbsHierarchies,
  productPage = false,
  className,
}: {
  /** List of hierarchies return by OctipasAPI */
  breadCrumbsHierarchies?: Hierarchy[] | ProductBreadcrumbType[];
  /** Product page or not. */
  productPage?: boolean;
  /** Additional classes */
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [ref, setRef] = useState<HTMLOListElement | null>(null);
  const breadCrumbsHierarchiesCount = breadCrumbsHierarchies?.length || 0;
  const breadCrumbsHierarchiesSliced = useMemo(
    () => breadCrumbsHierarchies?.slice(1) || [],
    [breadCrumbsHierarchies]
  );

  const {
    scrollableRef,
    handleMouseDown,
  } = useHorizontalScroll<HTMLOListElement>();

  scrollableRef.current = ref;

  const [showNext, setShowNext] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const breadcrumbScrollWidth = ref ? ref.scrollWidth - ref.clientWidth : 0;

  const handleGtmEvent = (label: string) => {
    pushGtmEvent({
      event: '_click_other',
      category: 'clic autre - fiche produit',
      element: 'navigation',
      detail: label,
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!ref) return;

      setShowPrevious(ref.scrollLeft > 0);
      setShowNext(ref.scrollLeft < breadcrumbScrollWidth);
    };

    handleScroll();
    ref?.addEventListener('scroll', handleScroll);
    return () => {
      ref?.removeEventListener('scroll', handleScroll);
    };
  }, [ref]);

  return (
    <>
      <div className={classnames(styles.breadCrumbs, className)}>
        <ol onMouseDown={handleMouseDown} ref={ref => setRef(ref)}>
          <li>
            <Link
              to={getUri('/')}
              key={`breadcrumb-homepage`}
              className={classnames({
                [styles.boldHover]: productPage,
              })}
              onClick={() => {
                handleGtmEvent(capitalize(t('LABEL_HOME', { lng: 'fr' })));
              }}
            >
              <span>{capitalize(t('LABEL_HOME'))}</span>
            </Link>
          </li>
          {breadCrumbsHierarchiesSliced.map((hierarchy, index) => {
            const {
              Url: url,
              Label: labelUrl,
              Id: id,
              Description: customLabel,
            } = hierarchy;
            const { MenuNoClick } = getFormattedMetaData(hierarchy?.Metadata);
            const disableClick = MenuNoClick?.Value === 'True';
            const isLastElement = breadCrumbsHierarchiesCount === index + 2;
            const label = customLabel ? customLabel : labelUrl;
            return (
              <li key={`breadcrumb-${label}-${id}`}>
                <Link
                  to={getUri(url)}
                  className={classnames({
                    [styles.disabled]: !url || disableClick,
                    [styles.boldHover]: productPage,
                  })}
                  onClick={() => {
                    handleGtmEvent(capitalize(label));
                  }}
                >
                  <span>{isLastElement ? label : capitalize(label)}</span>
                </Link>
              </li>
            );
          })}
        </ol>
        {showPrevious && (
          <button
            className={classnames(styles.scrollArrow, styles.leftArrow)}
            onClick={() => {
              if (ref) ref.scrollLeft = 0;
            }}
          >
            <ArrowBackIosIcon className={styles.icon} />
          </button>
        )}
        {showNext && (
          <button
            className={classnames(styles.scrollArrow, styles.rightArrow)}
            onClick={() => {
              if (ref) ref.scrollLeft = breadcrumbScrollWidth;
            }}
          >
            <ArrowForwardIosIcon className={styles.icon} />
          </button>
        )}
      </div>
      <BreadCrumbsJsonLd breadcrumbItems={breadCrumbsHierarchiesSliced} />
    </>
  );
}
